import React, { useEffect, useState } from "react";
import api, { getErrorDetail, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [captionZh, setCaptionZh] = useState("");
  const [uploading, setUploading] = useState(false);

  async function load() {
    try { const { data } = await api.get("/gallery?limit=120"); setItems(data); } catch { /* ignore */ }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function submit(e) {
    e.preventDefault();
    if (!file) { toast.error("Pick an image"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("caption", caption); fd.append("caption_zh", captionZh); fd.append("file", file);
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      setCaption(""); setCaptionZh(""); setFile(null);
      const el = document.getElementById("gallery-file-input"); if (el) el.value = "";
      load();
    } catch(e) { toast.error(getErrorDetail(e)); }
    finally { setUploading(false); }
  }

  async function del(id) {
    if (!window.confirm("Delete this photo?")) return;
    try { await api.delete(`/gallery/${id}`); toast.success("Deleted"); load(); }
    catch(e){ toast.error(getErrorDetail(e)); }
  }

  return (
    <div data-testid="admin-gallery-panel" className="space-y-8">
      <form onSubmit={submit} className="bg-white border border-[hsl(var(--border))] rounded-2xl p-6 space-y-4" data-testid="admin-gallery-form">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Upload a photo (JPG / PNG / WEBP · max 8 MB · duplicates blocked by content hash)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Caption (English)</Label><Input value={caption} onChange={(e)=>setCaption(e.target.value)} data-testid="gallery-caption" className="mt-1" /></div>
          <div><Label>Caption (中文)</Label><Input value={captionZh} onChange={(e)=>setCaptionZh(e.target.value)} data-testid="gallery-caption-zh" className="mt-1" /></div>
          <div className="md:col-span-2"><Label>Image file</Label>
            <input id="gallery-file-input" type="file" required accept="image/jpeg,image/png,image/webp" onChange={(e)=>setFile(e.target.files?.[0]||null)} data-testid="gallery-file" className="mt-1 block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[hsl(var(--muted))] file:px-4 file:py-2 file:text-xs file:tracking-[0.15em] file:uppercase file:text-[hsl(var(--foreground))]" />
          </div>
        </div>
        <Button type="submit" disabled={uploading} data-testid="gallery-submit" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">{uploading ? "Uploading…" : "Upload photo"}</Button>
      </form>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-gallery-grid">
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-gallery-row-${p.id}`} className="relative group rounded-xl overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--muted))]">
            <img src={`${API_BASE}/gallery/${p.id}/image`} alt={p.caption || ""} className="w-full aspect-square object-cover" loading="lazy" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 flex items-end justify-between gap-2">
              <p className="text-xs text-white line-clamp-2">{p.caption || "—"}</p>
              <button type="button" onClick={()=>del(p.id)} data-testid={`admin-gallery-delete-${p.id}`} className="text-white/80 hover:text-white"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full p-8 text-center text-[hsl(var(--muted-foreground))]">No photos yet.</p>}
      </div>
    </div>
  );
}
